import React, { useState, useEffect } from 'react';
import { uploadFiles } from '../services/dataFetch';
import './NgarkoWebshop.css';
import { formatDateTimeDDMMYYYY } from '../utils/dateUtils';

const STORAGE_KEY = 'goethe_exam_results';

const NgarkoWebshop = () => {
  const [olympFile, setOlympFile] = useState(null);
  const [webshopFile, setWebshopFile] = useState(null);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [storedData, setStoredData] = useState(null);

  // Load and parse data from localStorage on component mount
  useEffect(() => {
    const loadStoredData = () => {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          if (parsedData && Array.isArray(parsedData.data)) {
            setStoredData(parsedData);
            setMessage(`Loaded ${parsedData.data.length} records from storage`);
          }
        } catch (e) {
          console.error('Failed to parse saved data:', e);
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    };
    loadStoredData();
  }, []);

  const handleOlympChange = (e) => {
    setOlympFile(e.target.files[0]);
  };

  const handleWebshopChange = (e) => {
    setWebshopFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!olympFile || !webshopFile) {
      setMessage('Please upload both files');
      return;
    }

    setIsLoading(true);
    setMessage('Processing files...');

    try {
      const result = await uploadFiles(olympFile, webshopFile);

      // Include exam_id in stored data
      const newData = {
        timestamp: new Date().toISOString(),
        exam_id: result.exam_id,
        data: result.data
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
      setStoredData(newData);
      setMessage(`Success! Processed ${result.data.length} records`);
    } catch (error) {
      setMessage(error.message || 'Error processing files');
    } finally {
      setIsLoading(false);
    }
  };

  const clearStorage = () => {
    localStorage.removeItem(STORAGE_KEY);
    setStoredData(null);
    setMessage('Cleared saved data');
  };

  // Function to render table headers
  const renderTableHeaders = () => {
    if (!storedData?.data || storedData.data.length === 0) return null;
    
    const headers = Object.keys(storedData.data[0]);
    return headers.map((header) => (
      <th key={header}>{header}</th>
    ));
  };

  const renderTableRows = () => {
    if (!storedData?.data) return null;
    
    return storedData.data.map((row, index) => (
      <tr key={index}>
        {Object.values(row).map((value, i) => (
          <td key={i}>
            {value === null || value === undefined ? '' : value.toString()}
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <div className="file-upload-container">
      <h2>Upload Files</h2>
      <form onSubmit={handleSubmit}>
        <div className="file-input-group">
          <label>
            Olymp File:
            <input 
              type="file" 
              accept=".xlsx" 
              onChange={handleOlympChange} 
              required
            />
          </label>
        </div>
        
        <div className="file-input-group">
          <label>
            Webshop File:
            <input 
              type="file" 
              accept=".xlsx" 
              onChange={handleWebshopChange} 
              required
            />
          </label>
        </div>

        <div className="button-group">
          <button type="submit" disabled={isLoading}>
            {isLoading ? 'Processing...' : 'Upload and Process'}
          </button>
          {storedData && (
            <button 
              type="button" 
              onClick={clearStorage}
              className="clear-button"
            >
              Clear Saved Data
            </button>
          )}
        </div>
        
        {message && <div className={`message ${storedData ? 'success' : ''}`}>{message}</div>}
      </form>

      {storedData?.data && (
        <div className="results-container">
          <h3>
            Processed Results ({storedData.data.length} records)
            <span className="timestamp">
              {formatDateTimeDDMMYYYY(storedData.timestamp)}
            </span>
          </h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  {renderTableHeaders()}
                </tr>
              </thead>
              <tbody>
                {renderTableRows()}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default NgarkoWebshop;